/**
 * ProgramGeneratorWizard Component
 *
 * AI-powered multi-step wizard for generating personalized workout programs.
 * Steps: Select Client -> Review Profile -> Configure Program -> Generate -> Review
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import {
  ChevronLeft,
  ChevronRight,
  Check,
  User,
  Target,
  Dumbbell,
  Wand2,
  FileText,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  ChevronsUpDown,
  Sparkles,
  Calendar,
  Layers,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useOrganization } from '@/hooks/use-organization';
import { unwrapFunctionInvoke, getFriendlyMessage, reportError } from '@/lib/error-utils';
import {
  useClientFitnessProfile,
  ClientFitnessProfile,
  isProfileComplete,
  getProfileCompletionPercentage,
} from '@/hooks/use-client-fitness-profile';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import FitnessProfileWizard from './FitnessProfileWizard';

// Step configuration
const STEPS = [
  {
    id: 'client',
    title: 'Select Client',
    description: 'Choose a client to create a program for',
    icon: User,
  },
  {
    id: 'profile',
    title: 'Review Profile',
    description: 'Verify fitness profile and limitations',
    icon: FileText,
  },
  {
    id: 'configure',
    title: 'Configure Program',
    description: 'Set program parameters',
    icon: Target,
  },
  {
    id: 'generate',
    title: 'Generate',
    description: 'AI creates your program',
    icon: Wand2,
  },
  {
    id: 'review',
    title: 'Review & Save',
    description: 'Review and save the program',
    icon: CheckCircle2,
  },
] as const;

// Split type options
const SPLIT_OPTIONS = [
  { value: 'full_body', label: 'Full Body', days: 3, description: 'Train all muscles each session' },
  { value: 'upper_lower', label: 'Upper/Lower', days: 4, description: 'Alternate upper and lower body' },
  { value: 'ppl', label: 'Push/Pull/Legs', days: 3, description: 'Classic bodybuilding split' },
  { value: 'push_pull', label: 'Push/Pull', days: 4, description: 'Upper body focus split' },
  { value: 'bro_split', label: 'Body Part', days: 5, description: 'One muscle group per day' },
];

// Goal options
const GOAL_OPTIONS = [
  { value: 'strength', label: 'Strength', description: 'Build maximal force production' },
  { value: 'hypertrophy', label: 'Muscle Growth', description: 'Maximize muscle size' },
  { value: 'fat_loss', label: 'Fat Loss', description: 'Preserve muscle while cutting' },
  { value: 'endurance', label: 'Endurance', description: 'Improve cardiovascular fitness' },
  { value: 'general_fitness', label: 'General Fitness', description: 'Balanced approach' },
];

// Duration options (weeks)
const DURATION_OPTIONS = [4, 6, 8, 10, 12, 16];

// Experience labels
const EXPERIENCE_LABELS: Record<string, string> = {
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
  elite: 'Elite',
};

// Archetype labels — mirrors archetypeLabel() in use-pattern-memory.ts.
// Used in provenance badges ("From how you usually program Push").
const ARCHETYPE_LABELS: Record<string, string> = {
  push: 'Push',
  pull: 'Pull',
  legs: 'Legs',
  upper: 'Upper',
  lower: 'Lower',
  full_body: 'Full body',
  arms: 'Arms',
  core: 'Core',
  cardio: 'Cardio',
  unknown: 'this day',
};

function archetypeLabel(archetype?: string): string {
  if (!archetype) return 'this day';
  return ARCHETYPE_LABELS[archetype] ?? 'this day';
}

// Goal labels
const GOAL_LABELS: Record<string, string> = {
  strength: 'Build Strength',
  hypertrophy: 'Build Muscle',
  fat_loss: 'Lose Fat',
  endurance: 'Improve Endurance',
  athletic_performance: 'Athletic Performance',
  general_fitness: 'General Fitness',
  rehabilitation: 'Rehabilitation',
};

interface Client {
  id: string;
  name: string;
  email: string | null;
  avatar_url: string | null;
}

// Shape returned by the generateWorkoutProgram tool (edge fn). The tool
// persists program + day rows and returns a preview of each workout day.
type DaySource = "pattern" | "starter";

interface GeneratedDay {
  name: string;
  /** Provenance: from the trainer's own patterns vs the generic engine. */
  source?: DaySource;
  archetype?: string;
  exerciseCount: number;
  exercises?: string[];
}

interface GeneratedProgram {
  programId: string;
  programName: string;
  clientName: string;
  splitType: string;
  daysPerWeek: number;
  durationWeeks: number;
  goal: string | null;
  /** How many days were built from the trainer's programming patterns. */
  patternDayCount?: number;
  exerciseCount: number;
  days: GeneratedDay[];
  notes?: string;
  message?: string;
}

interface ProgramGeneratorWizardProps {
  onComplete?: (programId: string) => void;
  onCancel?: () => void;
  preselectedClientId?: string;
}

export default function ProgramGeneratorWizard({
  onComplete,
  onCancel,
  preselectedClientId,
}: ProgramGeneratorWizardProps) {
  // Active org — passed through to executeTool so the generated program is
  // stamped with the TRAINER's active org (not the client's). use-programs
  // filters the Programs list by active org, so without this the generated
  // program appears in the wrong org and is invisible in the list. Matches
  // the pattern in src/hooks/use-ai-agent.ts.
  const { organization, allMemberships, hasMultipleOrgs, switchOrganization } = useOrganization();

  // Step state
  const [currentStep, setCurrentStep] = useState(0);

  // Client selection
  const [selectedClientId, setSelectedClientId] = useState<string | null>(preselectedClientId || null);
  const [clientSearchOpen, setClientSearchOpen] = useState(false);

  // Program configuration
  const [programName, setProgramName] = useState('');
  const [splitType, setSplitType] = useState('ppl');
  const [durationWeeks, setDurationWeeks] = useState(8);
  const [goalFocus, setGoalFocus] = useState('');
  const [includeDeloads, setIncludeDeloads] = useState(true);
  const [includePeriodization, setIncludePeriodization] = useState(true);

  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedProgram, setGeneratedProgram] = useState<GeneratedProgram | null>(null);
  const [generationError, setGenerationError] = useState<string | null>(null);

  // Inline "create fitness profile" dialog (shown when the selected client
  // has no profile yet — otherwise the wizard dead-ends on step 2).
  const [profileWizardOpen, setProfileWizardOpen] = useState(false);

  // Fetch clients
  // Scope client list to the trainer's ACTIVE org. Programs belong to the org
  // they're generated in, so showing clients from other orgs here just produces
  // cross-org confusion (a generated program would land in the picked client's
  // org instead of the active one, then be invisible in the Programs list).
  // If the trainer wants to create a program in a different org, they switch
  // orgs via the org switcher and reopen this wizard — the queryKey includes
  // organization?.id so the client list refetches on switch.
  const { data: clients = [], isLoading: clientsLoading } = useQuery({
    queryKey: ['clients-for-program-generator', organization?.id],
    enabled: !!organization?.id,
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !organization?.id) return [];

      const { data, error } = await supabase
        .from('clients')
        .select('id, name, email, avatar_url')
        .eq('user_id', user.id)
        .eq('organization_id', organization.id)
        .eq('status', 'active')
        .order('name');

      if (error) throw error;
      return (data || []) as Client[];
    },
  });

  // Fetch selected client's fitness profile
  const {
    profile,
    isLoading: profileLoading,
    refetch: refetchProfile,
  } = useClientFitnessProfile({ clientId: selectedClientId || '' });

  // Set goal focus from profile when it loads
  useEffect(() => {
    if (profile?.primary_goal && !goalFocus) {
      setGoalFocus(profile.primary_goal);
    }
  }, [profile, goalFocus]);

  // Auto-generate program name — defaults to "{Client}'s Program".
  useEffect(() => {
    const client = clients.find((c) => c.id === selectedClientId);
    if (client && !programName) {
      setProgramName(`${client.name}'s Program`);
    }
  }, [selectedClientId, clients, programName]);

  const selectedClient = clients.find((c) => c.id === selectedClientId);
  const profileComplete = profile ? isProfileComplete(profile) : false;
  const profileCompletion = profile ? getProfileCompletionPercentage(profile) : 0;

  // Generate program mutation
  const generateProgram = useMutation({
    mutationFn: async () => {
      if (!selectedClientId) throw new Error('No client selected');

      setIsGenerating(true);
      setGenerationError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Call the AI agent edge function. unwrapFunctionInvoke surfaces both
      // the transport error AND the nested `data.error` a failing tool returns
      // (e.g. "no exercises match the client's equipment") as an AppError that
      // carries the real cause + code — instead of swallowing it as a generic
      // "non-2xx status code".
      const result = await supabase.functions.invoke('ai-agent', {
        body: {
          action: 'executeTool',
          // Pass the trainer's ACTIVE org so generateWorkoutProgram stamps the
          // program + workout templates with it (otherwise the tool falls back
          // to the client's org and the program is invisible in the trainer's
          // Programs list).
          organizationId: organization?.id,
          toolName: 'generateWorkoutProgram',
          toolParams: {
            clientId: selectedClientId,
            programName,
            splitType,
            durationWeeks,
            goalFocus: goalFocus || undefined,
            includeDeloads,
            includePeriodization,
          },
        },
      });

      const data = unwrapFunctionInvoke<{ result: GeneratedProgram }>(result);
      return data.result;
    },
    onSuccess: (data) => {
      // Defensive: a malformed result (missing days) must not crash the review
      // render into the app-wide "Oops" error boundary. Treat it as a failure.
      if (!data || !Array.isArray(data.days)) {
        setIsGenerating(false);
        const msg = "The program came back incomplete. Try generating again.";
        setGenerationError(msg);
        reportError(new Error("generateWorkoutProgram returned no days"), {
          context: 'ai-program-generate',
        });
        return;
      }
      setGeneratedProgram(data);
      setIsGenerating(false);
      toast.success('Program generated');
      // Auto-advance to review step
      setCurrentStep(4);
    },
    onError: (error: Error) => {
      // Show the real, friendly cause inline (the wizard's error panel) AND
      // as a toast carrying a copyable (code: xxx) breadcrumb + Sentry log.
      const friendly = getFriendlyMessage(error);
      setGenerationError(friendly.userMessage);
      setIsGenerating(false);
      reportError(error, { context: 'ai-program-generate' });
    },
  });

  // Navigation
  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      if (currentStep === 3) {
        // Generate step - trigger generation
        generateProgram.mutate();
      } else {
        setCurrentStep((prev) => prev + 1);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleComplete = () => {
    if (generatedProgram?.programId) {
      onComplete?.(generatedProgram.programId);
    }
  };

  // Step validation
  const isStepValid = () => {
    switch (currentStep) {
      case 0: // Client selection
        return !!selectedClientId;
      case 1: // Profile review
        return profileComplete;
      case 2: // Configure
        return programName.trim() && splitType && durationWeeks && goalFocus;
      case 3: // Generate
        return true;
      case 4: // Review
        return !!generatedProgram;
      default:
        return true;
    }
  };

  const currentStepConfig = STEPS[currentStep];
  const progress = ((currentStep + 1) / STEPS.length) * 100;
  const isLastStep = currentStep === STEPS.length - 1;

  return (
    <div className="space-y-6">
      {/* Progress Header */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">{currentStepConfig.title}</h2>
            <p className="text-sm text-muted-foreground">{currentStepConfig.description}</p>
          </div>
          <Badge variant="secondary">
            Step {currentStep + 1} of {STEPS.length}
          </Badge>
        </div>
        <Progress value={progress} className="h-2" />

        {/* Step Indicators */}
        <div className="flex items-center justify-center gap-2">
          {STEPS.map((step, index) => {
            const Icon = step.icon;
            const isActive = index === currentStep;
            const isComplete = index < currentStep;

            return (
              <div
                key={step.id}
                className={cn(
                  'flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors',
                  isActive && 'border-primary bg-primary text-primary-foreground',
                  isComplete && 'border-primary bg-primary/20 text-primary',
                  !isActive && !isComplete && 'border-muted bg-muted/50 text-muted-foreground'
                )}
              >
                {isComplete ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
              </div>
            );
          })}
        </div>
      </div>

      {/* Step Content */}
      <Card>
        <CardContent className="pt-6">
          {/* Step 1: Client Selection */}
          {currentStep === 0 && (
            <div className="space-y-6">
              {/* Org pill — clients + the resulting program are scoped to the
                  active org. Trainers with multiple orgs can switch here without
                  leaving the wizard. */}
              {hasMultipleOrgs ? (
                <div className="flex items-center justify-between rounded-md border border-border bg-muted/40 px-3 py-2 text-xs">
                  <span className="text-muted-foreground">
                    Creating in <span className="font-medium text-foreground">{organization?.name ?? "—"}</span>
                  </span>
                  <Select
                    value={organization?.id ?? ""}
                    onValueChange={(orgId) => {
                      if (orgId && orgId !== organization?.id) switchOrganization(orgId);
                    }}
                  >
                    <SelectTrigger className="h-7 w-auto gap-1 border-0 bg-transparent px-2 text-xs hover:bg-muted">
                      <SelectValue placeholder="Switch org" />
                    </SelectTrigger>
                    <SelectContent>
                      {allMemberships.map((m) => (
                        <SelectItem key={m.organization_id} value={m.organization_id} className="text-xs">
                          {m.organization?.name ?? m.organization_id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}

              <div className="space-y-3">
                <Label className="text-sm font-medium">Select a Client *</Label>
                <p className="text-xs text-muted-foreground">
                  Choose the client you want to create a program for
                </p>

                <Popover open={clientSearchOpen} onOpenChange={setClientSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={clientSearchOpen}
                      className="w-full justify-between"
                      disabled={clientsLoading}
                    >
                      {clientsLoading ? (
                        <span className="text-muted-foreground">Loading clients...</span>
                      ) : selectedClient ? (
                        <span>{selectedClient.name}</span>
                      ) : (
                        <span className="text-muted-foreground">Search for a client...</span>
                      )}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[400px] p-0">
                    <Command>
                      <CommandInput placeholder="Search clients..." />
                      <CommandList>
                        <CommandEmpty>No clients found.</CommandEmpty>
                        <CommandGroup>
                          {clients.map((client) => (
                            <CommandItem
                              key={client.id}
                              value={client.name}
                              onSelect={() => {
                                setSelectedClientId(client.id);
                                setClientSearchOpen(false);
                                setProgramName(''); // Reset program name for new client
                              }}
                            >
                              <Check
                                className={cn(
                                  'mr-2 h-4 w-4',
                                  selectedClientId === client.id ? 'opacity-100' : 'opacity-0'
                                )}
                              />
                              <div className="flex flex-col">
                                <span>{client.name}</span>
                                {client.email && (
                                  <span className="text-xs text-muted-foreground">{client.email}</span>
                                )}
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {selectedClient && (
                <Card className="bg-muted/30">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                        <User className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{selectedClient.name}</p>
                        <p className="text-sm text-muted-foreground">{selectedClient.email}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Step 2: Profile Review */}
          {currentStep === 1 && (
            <div className="space-y-6">
              {profileLoading ? (
                <div className="flex items-center justify-center p-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : !profile ? (
                <div className="flex flex-col items-center justify-center p-8 rounded-lg border border-dashed">
                  <AlertTriangle className="h-12 w-12 text-warning mb-4" />
                  <h3 className="font-semibold text-lg mb-2">No Fitness Profile Found</h3>
                  <p className="text-sm text-muted-foreground text-center max-w-md">
                    This client doesn't have a fitness profile yet. A complete profile helps the AI
                    create a more personalized and effective program.
                  </p>
                  <Button
                    className="mt-4"
                    variant="outline"
                    onClick={() => setProfileWizardOpen(true)}
                  >
                    Create Profile
                  </Button>
                </div>
              ) : (
                <>
                  {/* Profile Completion */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">Profile Completion</Label>
                      <Badge variant={profileComplete ? 'default' : 'secondary'}>
                        {profileCompletion}%
                      </Badge>
                    </div>
                    <Progress value={profileCompletion} className="h-2" />
                    {!profileComplete && (
                      <p className="text-xs text-warning">
                        Complete the profile for better program recommendations
                      </p>
                    )}
                  </div>

                  <Separator />

                  {/* Profile Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Experience</Label>
                      <p className="font-medium">
                        {profile.training_experience
                          ? EXPERIENCE_LABELS[profile.training_experience]
                          : 'Not set'}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Training Years</Label>
                      <p className="font-medium">
                        {profile.training_years ?? 'Not set'} {profile.training_years === 1 ? 'year' : 'years'}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Primary Goal</Label>
                      <p className="font-medium">
                        {profile.primary_goal ? GOAL_LABELS[profile.primary_goal] : 'Not set'}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Session Duration</Label>
                      <p className="font-medium">
                        {profile.preferred_session_duration
                          ? `${profile.preferred_session_duration} minutes`
                          : 'Not set'}
                      </p>
                    </div>
                  </div>

                  <Separator />

                  {/* Equipment */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Available Equipment</Label>
                    {profile.available_equipment.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {profile.available_equipment.slice(0, 10).map((eq) => (
                          <Badge key={eq} variant="secondary" className="text-xs">
                            {eq.replace(/_/g, ' ')}
                          </Badge>
                        ))}
                        {profile.available_equipment.length > 10 && (
                          <Badge variant="outline" className="text-xs">
                            +{profile.available_equipment.length - 10} more
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">No equipment selected</p>
                    )}
                  </div>

                  {/* Injuries */}
                  {profile.injuries.length > 0 && (
                    <>
                      <Separator />
                      <div className="space-y-2">
                        <Label className="text-sm font-medium flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 text-warning" />
                          Active Injuries
                        </Label>
                        <div className="space-y-1">
                          {profile.injuries
                            .filter((i) => i.is_active)
                            .map((injury, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-sm">
                                <Badge
                                  variant={injury.severity === 'severe' ? 'destructive' : 'secondary'}
                                >
                                  {injury.severity}
                                </Badge>
                                <span>{injury.area}</span>
                              </div>
                            ))}
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {/* Step 3: Configure Program */}
          {currentStep === 2 && (
            <div className="space-y-6">
              {/* Program Name */}
              <div className="space-y-2">
                <Label htmlFor="program-name">Program Name *</Label>
                <Input
                  id="program-name"
                  value={programName}
                  onChange={(e) => setProgramName(e.target.value)}
                  placeholder="e.g., 12-Week Hypertrophy Program"
                />
              </div>

              {/* Goal Focus */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Goal Focus *</Label>
                <RadioGroup
                  value={goalFocus}
                  onValueChange={setGoalFocus}
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
                >
                  {GOAL_OPTIONS.map((option) => (
                    <div key={option.value}>
                      <RadioGroupItem
                        value={option.value}
                        id={`goal-${option.value}`}
                        className="peer sr-only"
                      />
                      <Label
                        htmlFor={`goal-${option.value}`}
                        className={cn(
                          'flex flex-col p-4 rounded-lg border-2 cursor-pointer transition-all',
                          'hover:bg-muted/50',
                          'peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5'
                        )}
                      >
                        <span className="font-medium">{option.label}</span>
                        <span className="text-xs text-muted-foreground">{option.description}</span>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {/* Split Type */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Training Split *</Label>
                <RadioGroup
                  value={splitType}
                  onValueChange={setSplitType}
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
                >
                  {SPLIT_OPTIONS.map((option) => (
                    <div key={option.value}>
                      <RadioGroupItem
                        value={option.value}
                        id={`split-${option.value}`}
                        className="peer sr-only"
                      />
                      <Label
                        htmlFor={`split-${option.value}`}
                        className={cn(
                          'flex flex-col p-4 rounded-lg border-2 cursor-pointer transition-all',
                          'hover:bg-muted/50',
                          'peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5'
                        )}
                      >
                        <span className="font-medium">{option.label}</span>
                        <span className="text-xs text-muted-foreground">{option.description}</span>
                        <Badge variant="outline" className="mt-2 w-fit">
                          {option.days} days/week
                        </Badge>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {/* Duration & Options */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="duration">Program Duration *</Label>
                  <Select
                    value={durationWeeks.toString()}
                    onValueChange={(value) => setDurationWeeks(parseInt(value))}
                  >
                    <SelectTrigger id="duration">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DURATION_OPTIONS.map((weeks) => (
                        <SelectItem key={weeks} value={weeks.toString()}>
                          {weeks} weeks
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Include Deload Weeks</Label>
                  <Select
                    value={includeDeloads ? 'yes' : 'no'}
                    onValueChange={(value) => setIncludeDeloads(value === 'yes')}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes (Recommended)</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Periodization</Label>
                  <Select
                    value={includePeriodization ? 'yes' : 'no'}
                    onValueChange={(value) => setIncludePeriodization(value === 'yes')}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes (Recommended)</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Generate */}
          {currentStep === 3 && (
            <div className="flex flex-col items-center justify-center py-12">
              {isGenerating ? (
                <>
                  <div className="relative">
                    <Sparkles className="h-16 w-16 text-primary animate-pulse" />
                    <Loader2 className="absolute inset-0 h-16 w-16 animate-spin text-primary/30" />
                  </div>
                  <h3 className="text-lg font-semibold mt-6">Generating Your Program</h3>
                  <p className="text-sm text-muted-foreground text-center max-w-md mt-2">
                    Spotter AI is creating a personalized {durationWeeks}-week program based on{' '}
                    {selectedClient?.name}'s profile...
                  </p>
                  <div className="flex items-center gap-6 mt-6 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Dumbbell className="h-4 w-4" />
                      Selecting exercises
                    </div>
                    <div className="flex items-center gap-2">
                      <Layers className="h-4 w-4" />
                      Building periodization
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Scheduling workouts
                    </div>
                  </div>
                </>
              ) : generationError ? (
                <>
                  <AlertTriangle className="h-16 w-16 text-destructive" />
                  <h3 className="text-lg font-semibold mt-6">Generation Failed</h3>
                  <p className="text-sm text-destructive text-center max-w-md mt-2">
                    {generationError}
                  </p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => generateProgram.mutate()}
                  >
                    Try Again
                  </Button>
                </>
              ) : (
                <>
                  <Sparkles className="h-16 w-16 text-primary" />
                  <h3 className="text-lg font-semibold mt-6">Ready to Generate</h3>
                  <p className="text-sm text-muted-foreground text-center max-w-md mt-2">
                    Click "Generate Program" to create a personalized {durationWeeks}-week{' '}
                    {SPLIT_OPTIONS.find((s) => s.value === splitType)?.label} program for{' '}
                    {selectedClient?.name}.
                  </p>

                  {/* Configuration Summary */}
                  <Card className="mt-6 w-full max-w-md">
                    <CardContent className="pt-4">
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-muted-foreground">Program:</span>
                          <p className="font-medium">{programName}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Goal:</span>
                          <p className="font-medium">
                            {GOAL_OPTIONS.find((g) => g.value === goalFocus)?.label}
                          </p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Split:</span>
                          <p className="font-medium">
                            {SPLIT_OPTIONS.find((s) => s.value === splitType)?.label}
                          </p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Duration:</span>
                          <p className="font-medium">{durationWeeks} weeks</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </div>
          )}

          {/* Step 5: Review & Save */}
          {currentStep === 4 && generatedProgram && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-success/15">
                  <CheckCircle2 className="h-6 w-6 text-success" />
                </div>
                <div>
                  <h3 className="font-semibold">Program Generated Successfully!</h3>
                  <p className="text-sm text-muted-foreground">
                    Review the program below and save it to assign to {selectedClient?.name}.
                  </p>
                </div>
              </div>

              <Separator />

              {/* Pattern-match callout — only when at least one day came from
                  the trainer's own programming history. */}
              {(generatedProgram.patternDayCount ?? 0) > 0 && (
                <div className="flex items-start gap-3 rounded-lg border border-primary/30 bg-primary/5 p-4">
                  <Sparkles className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium">
                      {generatedProgram.patternDayCount} of {generatedProgram.days.length}{' '}
                      day{generatedProgram.days.length === 1 ? '' : 's'} built from your patterns
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Days marked below were shaped by how you usually program — your
                      exercises, your sets and reps. The rest are starter suggestions
                      you can customize.
                    </p>
                  </div>
                </div>
              )}

              {/* Program Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Program</span>
                    </div>
                    <p className="font-semibold mt-1">{generatedProgram.programName}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Duration</span>
                    </div>
                    <p className="font-semibold mt-1">{generatedProgram.durationWeeks} weeks</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <Dumbbell className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Days/Cycle</span>
                    </div>
                    <p className="font-semibold mt-1">{generatedProgram.days.length} days</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <Layers className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Exercises</span>
                    </div>
                    <p className="font-semibold mt-1">{generatedProgram.exerciseCount}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Program Days */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Program Structure</Label>
                <ScrollArea className="h-[300px] rounded-md border p-4">
                  <div className="space-y-2">
                    {generatedProgram.days.map((day, index) => (
                      <div
                        key={`${day.name}-${index}`}
                        className="flex flex-col gap-2 p-3 rounded-lg bg-muted/50 sm:flex-row sm:items-center sm:justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <Badge variant="outline">Day {index + 1}</Badge>
                          <span className="font-medium">{day.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {day.exerciseCount} exercise{day.exerciseCount === 1 ? '' : 's'}
                          </span>
                        </div>
                        {day.source === 'pattern' ? (
                          <Badge variant="default" className="w-fit gap-1">
                            <Sparkles className="h-3 w-3" />
                            From how you usually program {archetypeLabel(day.archetype)}
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="w-fit">
                            Starter suggestion — customize this
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={currentStep === 0 ? onCancel : handlePrevious}
          disabled={isGenerating}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          {currentStep === 0 ? 'Cancel' : 'Previous'}
        </Button>

        <div className="flex gap-2">
          {currentStep === 3 && !isGenerating && !generatedProgram ? (
            <Button onClick={handleNext} disabled={!isStepValid()}>
              <Sparkles className="h-4 w-4 mr-1" />
              Generate Program
            </Button>
          ) : isLastStep ? (
            <Button onClick={handleComplete} disabled={!generatedProgram}>
              <Check className="h-4 w-4 mr-1" />
              Save & Finish
            </Button>
          ) : (
            <Button onClick={handleNext} disabled={!isStepValid() || isGenerating}>
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>
      </div>

      {/* Inline fitness-profile creation — opened from the "No Fitness Profile
          Found" empty state on step 2. On completion we refetch so the profile
          appears and the step unblocks (Next was disabled with no profile). */}
      <Dialog open={profileWizardOpen} onOpenChange={setProfileWizardOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Create Fitness Profile{selectedClient ? ` — ${selectedClient.name}` : ''}
            </DialogTitle>
          </DialogHeader>
          {selectedClientId && (
            <FitnessProfileWizard
              clientId={selectedClientId}
              onComplete={() => {
                setProfileWizardOpen(false);
                refetchProfile();
              }}
              onCancel={() => setProfileWizardOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
